import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { format } from 'date-fns'

import { dateFnsLocale } from '@/utils/dateLocale'
import { Button } from '@/components/ui/Button'
import { refundLeadPayment } from '@/api/tbank'
import { leadCardCls } from '@/components/inspections/leadDetail'
import type { InspectionLeadRow } from '@/types/inspection-lead'

/**
 * Карточка «Платёж» (воронки с онлайн-оплатой).
 *
 * Все платёжные поля (payment_amount, payment_at, tbank_payment_id, refunded_at)
 * — read-only: на стороне БД триггер `inspection_leads_immutable_payment_check`
 * блокирует UPDATE после первичной установки. Источник истины — webhook от
 * Т-Банка (Status=CONFIRMED / REFUNDED) и edge function tbank-refund-payment.
 */
export function LeadPaymentCard({
  lead,
  onAfterRefund,
}: {
  lead: InspectionLeadRow
  onAfterRefund: () => void
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refundMut = useMutation({
    mutationFn: () => refundLeadPayment(lead.id),
    onSuccess: (data) => {
      if (!data.ok) {
        const reason =
          data.tbankMessage || data.tbankErrorCode || data.detail || data.reason || 'unknown'
        setError(t('leads.detail.refundFailed', { reason }))
        return
      }
      setError(null)
      setConfirming(false)
      onAfterRefund()
    },
    onError: (e: Error) => setError(e.message || 'invoke_failed'),
  })

  // Лид ещё не оплачен — «пустая» карточка с заглушкой, чтобы диспетчер видел
  // секцию и знал, что данные появятся после оплаты.
  if (!lead.payment_at) {
    return (
      <section className={leadCardCls}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="stryzh-heading-card">{t('leads.detail.secPayment')}</h2>
          <span className="rounded-full bg-[color:var(--stryzh-color-bg-elev)] px-3 py-1 text-xs font-semibold text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.detail.notPaid')}
          </span>
        </div>
        <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.detail.paymentPending')}
        </div>
      </section>
    )
  }

  // Реальный статус платежа (двухстадийка): резерв (холд) → списано → отменено.
  // payment_at стоит уже на холде, поэтому различаем по captured_at/refunded_at.
  const isRefunded = !!lead.refunded_at
  const isCaptured = !isRefunded && !!lead.tbank_captured_at
  const isHeld = !isRefunded && !isCaptured // деньги зарезервированы, не списаны
  const captureFailed = isHeld && !!lead.tbank_capture_failed_at
  const amountFormatted =
    lead.payment_amount != null
      ? new Intl.NumberFormat('ru-RU').format(Number(lead.payment_amount)) + ' ₽'
      : '—'
  const fmt = (iso: string) => format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: dateFnsLocale() })

  return (
    <section className={leadCardCls}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="stryzh-heading-card">{t('leads.detail.secPayment')}</h2>
        {isRefunded ? (
          <span className="rounded-full bg-[color:var(--stryzh-color-bg-elev)] px-3 py-1 text-xs font-semibold text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.detail.payReversed')}
          </span>
        ) : isCaptured ? (
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: 'rgba(74,222,128,.15)', color: 'var(--stryzh-color-success, #4ade80)' }}
          >
            {t('leads.detail.payCaptured')}
          </span>
        ) : (
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: 'rgba(251,191,36,.15)', color: 'var(--stryzh-color-warning, #fbbf24)' }}
          >
            {t('leads.detail.payHeld')}
          </span>
        )}
      </div>

      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        <dt className="text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.amount')}</dt>
        <dd className="font-mono">{amountFormatted}</dd>

        <dt className="text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.paymentDate')}</dt>
        <dd className="font-mono">{fmt(lead.payment_at)}</dd>

        {lead.tbank_payment_id ? (
          <>
            <dt className="text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.paymentId')}</dt>
            <dd className="min-w-0 break-all font-mono text-xs">{lead.tbank_payment_id}</dd>
          </>
        ) : null}

        {lead.tbank_captured_at ? (
          <>
            <dt className="text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.captureDate')}</dt>
            <dd className="font-mono">{fmt(lead.tbank_captured_at)}</dd>
          </>
        ) : null}

        {lead.refunded_at ? (
          <>
            <dt className="text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.refundDate')}</dt>
            <dd className="font-mono">{fmt(lead.refunded_at)}</dd>
          </>
        ) : null}
      </dl>

      {captureFailed ? (
        <div
          className="mt-3 rounded-md px-3 py-2 text-xs"
          style={{ background: 'rgba(248,113,113,.12)', color: 'var(--stryzh-color-danger, #f87171)' }}
        >
          {t('leads.detail.captureFailed')}
        </div>
      ) : isHeld ? (
        <div className="mt-3 text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.detail.holdHint')}
        </div>
      ) : null}

      {/* Связанный заказ COMPLETED — услуга оказана, возврат запрещён.
       *  Бэкенд тоже возвращает 409 order_completed. */}
      {!isRefunded && lead.tbank_payment_id && lead.order_status === 'COMPLETED' ? (
        <div className="mt-4 border-t border-[color:var(--stryzh-color-border-default)] pt-4 text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.detail.refundLocked')}
        </div>
      ) : !isRefunded && lead.tbank_payment_id ? (
        <div className="mt-4 border-t border-[color:var(--stryzh-color-border-default)] pt-4">
          {!confirming ? (
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              {t('leads.detail.refund')}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
                {t('leads.detail.refundConfirm', { amount: amountFormatted })}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  disabled={refundMut.isPending}
                  onClick={() => {
                    setError(null)
                    refundMut.mutate()
                  }}
                >
                  {refundMut.isPending ? t('leads.detail.refunding') : t('leads.detail.refundConfirmBtn')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={refundMut.isPending}
                  onClick={() => {
                    setConfirming(false)
                    setError(null)
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
              {error ? (
                <div className="rounded-md border border-[color:var(--stryzh-color-accent)] bg-[rgba(255,106,26,0.1)] px-3 py-2 text-xs text-[color:var(--stryzh-color-accent)]">
                  {error}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </section>
  )
}
